// GuideMeLockSheet.tsx — explainer shown when a non-member taps the "Guide me" tile.
// Styled to match ReminderNudgeSheet. "See membership" hands off to the usual unlock wall.
// House style: no emoji, no em-dash. Native: no mention of the website, prices or buying.
import React from 'react';
import { Btn } from '@/components/ui';
import { F, SIZE } from '@/new/theme';
import { ASK_FREE_LIMIT, readFreeRemaining } from '@/new/askFreeCache';

export function GuideMeLockSheet({ onSeeMembership, onClose, onTry, userId }: {
  onSeeMembership: () => void;
  onClose: () => void;
  /** Opens Ask for a non-member's free taster. The button is hidden when omitted. */
  onTry?: () => void;
  userId?: string | null;
}) {
  const left = readFreeRemaining(userId);
  const canTry = !!onTry && left !== 0;
  const tryLabel = left === null ? `Try it ${'·'} ${ASK_FREE_LIMIT} free questions` : `Try it ${'·'} ${left} free question${left === 1 ? '' : 's'} left`;
  return (
    <div
      style={overlay}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div role="dialog" aria-modal="true" aria-labelledby="guide-me-lock-title" style={panel}>
        <div id="guide-me-lock-title" style={{ fontFamily: F.title, fontSize: 22, fontWeight: 500, color: 'var(--ink)', marginBottom: 8 }}>
          Guide me
        </div>
        <p style={{ color: 'var(--ink-soft)', fontSize: SIZE.body, lineHeight: 1.55, margin: '0 0 18px', fontFamily: 'Inter, system-ui, sans-serif' }}>
          Tell it what{'’'}s going on and it points you to the lessons, tools and reads that fit, all created by a Clinical Psychologist. Each question uses a secure AI service that costs us to run, so Guide me is part of membership.
        </p>
        {canTry && (left === null || left === ASK_FREE_LIMIT) && (
          <p style={{ color: 'var(--ink-soft)', fontSize: SIZE.body, lineHeight: 1.55, margin: '-6px 0 18px', fontFamily: 'Inter, system-ui, sans-serif' }}>
            You can try it twice before deciding.
          </p>
        )}
        <Btn full size="lg" onClick={onSeeMembership}>See membership</Btn>
        {canTry && <button type="button" onClick={onTry} style={tryBtn}>{tryLabel}</button>}
        <button type="button" onClick={onClose} style={quietBtn}>Not now</button>
      </div>
    </div>
  );
}

const overlay: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  zIndex: 1100,
  background: 'rgba(20,16,12,0.55)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '24px',
  paddingTop: 'calc(var(--safe-top) + 24px)',
  paddingBottom: 'calc(var(--safe-bottom) + 24px)',
};
const panel: React.CSSProperties = {
  width: '100%',
  maxWidth: 400,
  background: 'var(--card-top, #fff)',
  border: '1px solid var(--hair-strong, rgba(0,0,0,0.1))',
  borderRadius: 20,
  padding: 24,
  maxHeight: '90vh',
  overflowY: 'auto',
};
const tryBtn: React.CSSProperties = {
  display: 'block',
  width: '100%',
  marginTop: 10,
  padding: '13px',
  border: '1.5px solid var(--hair-strong, rgba(0,0,0,0.18))',
  borderRadius: 999,
  background: 'none',
  color: 'var(--ink)',
  fontFamily: 'Inter, system-ui, sans-serif',
  fontSize: 15,
  fontWeight: 600,
  cursor: 'pointer',
};
const quietBtn: React.CSSProperties = {
  display: 'block',
  width: '100%',
  marginTop: 12,
  padding: '10px',
  border: 'none',
  background: 'none',
  color: 'var(--ink-soft)',
  fontFamily: 'Inter, system-ui, sans-serif',
  fontSize: 14,
  fontWeight: 600,
  cursor: 'pointer',
};
